/**
 * Pure pay-schedule engine — generates actual pay dates for a pay group and
 * counts how many fall in a window. Shared by server + client; no DB/React.
 *
 * This is the foundation for FSA proration (deduct an annual election over the
 * EXACT remaining pay periods of the plan year) and for accurate per-paycheck
 * display. Keep it pure and well-tested.
 *
 * All math is in UTC and date-only (time-of-day is ignored) to avoid timezone
 * drift between the server and browser.
 */

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly";

export interface PaySchedule {
  payFrequency: PayFrequency;
  /**
   * Anchor pay date (a known real payday, e.g. payrollSettings.periodStartDate).
   * Required for weekly/biweekly to be exact. For monthly it sets the day-of-month.
   * Semi-monthly ignores it (uses the 15th + last day convention).
   */
  anchorDate: Date | null;
}

const MS_PER_DAY = 86_400_000;

function startOfDayUTC(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function addDaysUTC(d: Date, days: number): Date {
  return new Date(d.getTime() + days * MS_PER_DAY);
}

/** Last calendar day (28–31) of the given UTC month. month is 0-based. */
function lastDayOfMonthUTC(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** [year, month] (month 0-based) for each month touched by [from, to]. */
function monthsBetween(from: Date, to: Date): Array<[number, number]> {
  const months: Array<[number, number]> = [];
  let year = from.getUTCFullYear();
  let month = from.getUTCMonth();
  const endYear = to.getUTCFullYear();
  const endMonth = to.getUTCMonth();
  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push([year, month]);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return months;
}

/**
 * Actual pay dates (UTC, date-only) where `from <= payday <= to`, inclusive.
 * Returns [] for an invalid window, or for weekly/biweekly with no anchor.
 */
export function generatePayDates(schedule: PaySchedule, from: Date, to: Date): Date[] {
  const fromDay = startOfDayUTC(from);
  const toDay = startOfDayUTC(to);
  if (fromDay.getTime() > toDay.getTime()) return [];

  switch (schedule.payFrequency) {
    case "weekly":
    case "biweekly": {
      if (!schedule.anchorDate) return []; // can't be exact without a known payday
      const interval = schedule.payFrequency === "weekly" ? 7 : 14;
      const anchor = startOfDayUTC(schedule.anchorDate);
      // Jump straight to the first payday >= from (anchor may be before or after).
      const offsetDays = Math.floor((fromDay.getTime() - anchor.getTime()) / MS_PER_DAY);
      const steps = Math.ceil(offsetDays / interval);
      let payday = addDaysUTC(anchor, steps * interval);
      while (payday.getTime() < fromDay.getTime()) payday = addDaysUTC(payday, interval);
      const dates: Date[] = [];
      while (payday.getTime() <= toDay.getTime()) {
        dates.push(payday);
        payday = addDaysUTC(payday, interval);
      }
      return dates;
    }

    case "semimonthly": {
      // Standard convention: the 15th and the last calendar day of each month.
      const dates: Date[] = [];
      for (const [year, month] of monthsBetween(fromDay, toDay)) {
        for (const day of [15, lastDayOfMonthUTC(year, month)]) {
          const d = new Date(Date.UTC(year, month, day));
          if (d.getTime() >= fromDay.getTime() && d.getTime() <= toDay.getTime()) dates.push(d);
        }
      }
      return dates;
    }

    case "monthly": {
      // Pay once a month on the anchor's day-of-month (clamped to the month's
      // last day); with no anchor, default to the last calendar day.
      const anchorDom = schedule.anchorDate ? startOfDayUTC(schedule.anchorDate).getUTCDate() : null;
      const dates: Date[] = [];
      for (const [year, month] of monthsBetween(fromDay, toDay)) {
        const lastDay = lastDayOfMonthUTC(year, month);
        const day = anchorDom == null ? lastDay : Math.min(anchorDom, lastDay);
        const d = new Date(Date.UTC(year, month, day));
        if (d.getTime() >= fromDay.getTime() && d.getTime() <= toDay.getTime()) dates.push(d);
      }
      return dates;
    }

    default:
      return [];
  }
}

/** How many pay dates fall in [from, to], inclusive. */
export function countRemainingPayPeriods(schedule: PaySchedule, from: Date, to: Date): number {
  return generatePayDates(schedule, from, to).length;
}
